import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Sparkles, Waves, Droplets, Sun, Wind, Heart, ShieldCheck, ArrowRight, Pencil, Save, RotateCcw, X, ArrowLeftRight, GraduationCap, Ban, Flower2, Rabbit, FlaskConical } from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

// Mapping route → slug
const ROUTE_TO_SLUG = {
  "/protocollo-cabina": "protocollo-cabina-odr",
  "/protocollo-parrucchieri": "protocollo-parrucchieri-odr",
  "/protocollo-cabina-inverno": "protocollo-cabina-inverno",
  "/protocollo-parrucchieri-inverno": "protocollo-parrucchieri-inverno",
};
const SLUG_TO_ROUTE = {
  "protocollo-cabina-odr": "/protocollo-cabina",
  "protocollo-parrucchieri-odr": "/protocollo-parrucchieri",
  "protocollo-cabina-inverno": "/protocollo-cabina-inverno",
  "protocollo-parrucchieri-inverno": "/protocollo-parrucchieri-inverno",
};

// Loghi università (CDN emergent assets — cross-environment safe)
const UNIVERSITA_LOGOS = [
  { name: "Università di Perugia (UNIPG)", url: "https://customer-assets.emergentagent.com/job_702651d9-ef70-48a7-aca7-36298387f6de/artifacts/f2orbeh7_logo%20Uni%20Perugia.png" },
  { name: "Università di Ferrara", url: "https://customer-assets.emergentagent.com/job_702651d9-ef70-48a7-aca7-36298387f6de/artifacts/fw9lygo2_logo%20Universit%C3%A0%20Ferrara_-_logo.svg.png" },
  { name: "Università di Bologna", url: "https://customer-assets.emergentagent.com/job_702651d9-ef70-48a7-aca7-36298387f6de/artifacts/r5el42uh_logo%20univ%20Bologna.png" },
  { name: "Università di Genova", url: "https://customer-assets.emergentagent.com/job_702651d9-ef70-48a7-aca7-36298387f6de/artifacts/8253pat1_logo%20univ%20genova.png" },
];

// Fallback quando il DB non ha sibling_slug/season_link_slug (es. Production DB non aggiornato)
const SIBLING_FALLBACK = {
  "protocollo-cabina-odr": { slug: "protocollo-parrucchieri-odr", label: "Vai al Protocollo per Parrucchieri" },
  "protocollo-parrucchieri-odr": { slug: "protocollo-cabina-odr", label: "Vai al Protocollo per Centri Estetici" },
  "protocollo-cabina-inverno": { slug: "protocollo-parrucchieri-inverno", label: "Vai al Protocollo Inverno Parrucchieri" },
  "protocollo-parrucchieri-inverno": { slug: "protocollo-cabina-inverno", label: "Vai al Protocollo Inverno Estetiste" },
};
const SEASON_FALLBACK = {
  "protocollo-cabina-odr": { slug: "protocollo-cabina-inverno", label: "Vai al Protocollo Invernale" },
  "protocollo-parrucchieri-odr": { slug: "protocollo-parrucchieri-inverno", label: "Vai al Protocollo Invernale" },
  "protocollo-cabina-inverno": { slug: "protocollo-cabina-odr", label: "Vai al Protocollo Estivo" },
  "protocollo-parrucchieri-inverno": { slug: "protocollo-parrucchieri-odr", label: "Vai al Protocollo Estivo" },
};

// SVG pittogramma "seal" tipo bollino cosmetico (cerchio doppio + testo circolare + icona centrale)
const Seal = ({ topText, bottomText, children, className = "" }) => {
  const id = React.useMemo(() => `seal-${Math.random().toString(36).slice(2, 8)}`, []);
  return (
    <svg viewBox="0 0 220 220" className={`w-full h-auto ${className}`} xmlns="http://www.w3.org/2000/svg">
      <defs>
        {/* Top arc: L to R through TOP (sweep=1 = CW) */}
        <path id={`${id}-top`} d="M 30 110 A 80 80 0 0 1 190 110" fill="none" />
        {/* Bottom arc: L to R through BOTTOM (sweep=0 = CCW) — text reads normally upright */}
        <path id={`${id}-bot`} d="M 30 110 A 80 80 0 0 0 190 110" fill="none" />
      </defs>
      <circle cx="110" cy="110" r="95" stroke="currentColor" strokeWidth="2.5" fill="none" />
      <circle cx="110" cy="110" r="83" stroke="currentColor" strokeWidth="1" fill="none" />
      <text fontSize="13" fontWeight="800" letterSpacing="2" fill="currentColor" style={{ textTransform: "uppercase" }}>
        <textPath href={`#${id}-top`} startOffset="50%" textAnchor="middle">{topText}</textPath>
      </text>
      <text fontSize="11.5" fontWeight="800" letterSpacing="1.6" fill="currentColor" style={{ textTransform: "uppercase" }} dy="10">
        <textPath href={`#${id}-bot`} startOffset="50%" textAnchor="middle">{bottomText}</textPath>
      </text>
      <circle cx="20" cy="110" r="2.5" fill="currentColor" />
      <circle cx="200" cy="110" r="2.5" fill="currentColor" />
      <g transform="translate(110 110)">{children}</g>
    </svg>
  );
};

const CERT_SEALS = [
  {
    topText: "Antibacterial",
    bottomText: "Formula",
    inner: (
      <g>
        <rect x="-18" y="-6" width="36" height="12" fill="currentColor" />
        <rect x="-6" y="-18" width="12" height="36" fill="currentColor" />
      </g>
    ),
  },
  {
    topText: "Nickel Tested",
    bottomText: "Testato Nickel",
    inner: (
      <text textAnchor="middle" dominantBaseline="central" fontSize="36" fontWeight="900" fill="currentColor" fontFamily="Georgia, serif">Ni</text>
    ),
  },
  {
    topText: "Dermatologically",
    bottomText: "Tested",
    inner: (
      <g fill="currentColor">
        {[
          [0, -14], [-12, -7], [12, -7], [-12, 7], [12, 7], [0, 14],
        ].map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="5" />
        ))}
      </g>
    ),
  },
  {
    topText: "Sensitive Skin",
    bottomText: "Pelle Sensibile",
    inner: (
      <path d="M 0 -20 C -14 -4 -14 12 0 20 C 14 12 14 -4 0 -20 Z" fill="currentColor" />
    ),
  },
];

const iconForCase = (n) => {
  const map = { 1: Wind, 2: Sun, 3: Waves, 4: Droplets, 5: Heart, 6: ShieldCheck };
  return map[n] || Sparkles;
};

const CERTIFICATIONS = [
  { Icon: GraduationCap, label: "Testato dalle Università" },
  { Icon: Ban, label: "Nickel Free" },
  { Icon: Flower2, label: "Pelli Sensibili" },
  { Icon: Rabbit, label: "Cruelty-free" },
  { Icon: FlaskConical, label: "Dermatologicamente Testato" },
];

const CertificationBadgesRow = ({ accentClass = "text-emerald-600", borderClass = "border-emerald-200", bgClass = "bg-emerald-50/60" }) => (
  <div className="mt-4 flex flex-wrap gap-2" data-testid="cert-badges-row">
    {CERTIFICATIONS.map(({ Icon, label }, idx) => (
      <div
        key={idx}
        className={`inline-flex items-center gap-1.5 ${bgClass} ${borderClass} border rounded-full px-2.5 py-1 shadow-sm`}
        title={label}
        data-testid={`cert-badge-${idx}`}
      >
        <Icon className={`w-3.5 h-3.5 ${accentClass}`} strokeWidth={2.25} />
        <span className="text-[11px] font-semibold text-slate-700 whitespace-nowrap">{label}</span>
      </div>
    ))}
  </div>
);

const EditableText = ({ value, onChange, editing, multiline = false, className = "", "data-testid": testId }) => {
  if (!editing) return <span className={className} data-testid={testId}>{value}</span>;
  return multiline ? (
    <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${className} bg-white/95 text-gray-900 min-h-[80px]`} data-testid={testId} />
  ) : (
    <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${className} bg-white/95 text-gray-900`} data-testid={testId} />
  );
};

const EditableImage = ({ url, onChange, editing, alt, className }) => {
  const [uploading, setUploading] = React.useState(false);
  const [dragOver, setDragOver] = React.useState(false);
  const inputRef = React.useRef(null);

  // Compressione client-side: ridimensiona a max 1600px lato lungo, JPEG 85%.
  // Ritorna una data URI base64 pronta da salvare nel documento MongoDB.
  // Vantaggi: (1) nessun limite proxy k8s (nulla viene inviato al backend);
  // (2) immagini persistono dopo deploy Preview→Production perché sono nel documento landing.
  const compressToDataURI = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Lettura file fallita"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("File non è un'immagine valida"));
      img.onload = () => {
        try {
          const maxSide = 1600;
          let { width: w, height: h } = img;
          if (Math.max(w, h) > maxSide) {
            if (w >= h) { h = Math.round(h * (maxSide / w)); w = maxSide; }
            else { w = Math.round(w * (maxSide / h)); h = maxSide; }
          }
          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d");
          ctx.fillStyle = "#ffffff"; // sfondo bianco per PNG con trasparenza
          ctx.fillRect(0, 0, w, h);
          ctx.drawImage(img, 0, 0, w, h);
          const dataUri = canvas.toDataURL("image/jpeg", 0.85);
          resolve(dataUri);
        } catch (err) { reject(err); }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

  const uploadFile = async (file) => {
    if (!file || !file.type?.startsWith("image/")) {
      toast.error("Il file non è un'immagine valida");
      return;
    }
    setUploading(true);
    try {
      const dataUri = await compressToDataURI(file);
      const sizeKb = Math.round((dataUri.length * 3 / 4) / 1024);
      onChange(dataUri);
      toast.success(`Immagine pronta (${sizeKb} KB). Ora clicca "Salva" per confermare.`);
    } catch (e) {
      toast.error("Errore elaborazione immagine: " + e.message);
    } finally {
      setUploading(false);
    }
  };

  // Paste dagli appunti
  const onPaste = (e) => {
    if (!editing) return;
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.type?.startsWith("image/")) {
        const file = it.getAsFile();
        if (file) { e.preventDefault(); uploadFile(file); return; }
      }
    }
  };

  return (
    <div
      className={`relative w-full h-full ${editing && dragOver ? "ring-4 ring-amber-400" : ""}`}
      onPaste={onPaste}
      onDragOver={(e) => { if (editing) { e.preventDefault(); setDragOver(true); } }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        if (!editing) return;
        e.preventDefault(); setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) uploadFile(f);
      }}
      tabIndex={editing ? 0 : -1}
    >
      <img src={url} alt={alt} className={className} loading="lazy" />
      {editing && (
        <>
          <div className="absolute top-2 right-2 flex gap-2 z-20">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg"
              data-testid="upload-img-btn"
            >
              {uploading ? "Caricamento..." : "📁 Carica"}
            </button>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f); e.target.value = ""; }}
            data-testid="file-input"
          />
          <div className="absolute bottom-2 left-2 right-2 bg-white/95 rounded-lg p-2 shadow-lg z-10 space-y-1">
            <Input
              value={url || ""}
              onChange={(e) => onChange(e.target.value)}
              placeholder="URL immagine o incolla (Ctrl+V) o trascina qui..."
              className="text-xs bg-white text-gray-900"
              data-testid="edit-img-url"
            />
            <p className="text-[10px] text-gray-500 leading-tight px-1">
              💡 Trascina un'immagine qui, oppure fai click e Ctrl+V per incollare uno screenshot
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default function ProtocolloCabinaLandingPage() {
  const location = useLocation();
  const SLUG = ROUTE_TO_SLUG[location.pathname] || "protocollo-cabina-odr";
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const { user, isAdmin } = useAuth();
  const admin = isAdmin || user?.role === "direzione";

  useEffect(() => {
    setData(null);
    axios.get(`${API}/landing/${SLUG}`).then((r) => setData(r.data)).catch(() => toast.error("Errore caricamento contenuti"));
  }, [SLUG]);

  const patch = (path, value) => {
    setData((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
      cur[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const patchArrayItem = (arrayPath, idx, subPath, value) => {
    setData((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const arr = arrayPath.split(".").reduce((o, k) => o[k], next);
      if (subPath) {
        const keys = subPath.split(".");
        let cur = arr[idx];
        for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
        cur[keys[keys.length - 1]] = value;
      } else {
        arr[idx] = value;
      }
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/landing/${SLUG}`, data, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Contenuti salvati");
      setEditing(false);
    } catch (e) {
      toast.error("Errore salvataggio: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const resetDefault = async () => {
    if (!window.confirm("Ripristinare i contenuti originali? Tutte le modifiche verranno perse.")) return;
    const token = localStorage.getItem("token");
    const r = await axios.post(`${API}/landing/${SLUG}/reset`, {}, { headers: { Authorization: `Bearer ${token}` } });
    setData(r.data);
    toast.success("Contenuti ripristinati");
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center text-teal-700">Caricamento...</div>;

  // Palette dinamica in base al tipo di landing
  const isParrucchieri = SLUG === "protocollo-parrucchieri-odr" || SLUG === "protocollo-parrucchieri-inverno";
  const isInverno = SLUG === "protocollo-cabina-inverno" || SLUG === "protocollo-parrucchieri-inverno";
  const theme = isInverno
    ? (isParrucchieri
      ? {
          pageBg: "bg-gradient-to-b from-indigo-50 via-white to-cyan-50",
          heroOverlay: "bg-gradient-to-r from-indigo-950/80 via-cyan-900/55 to-transparent",
          heroEyebrow: "text-cyan-200",
          heroSubtitle: "text-cyan-100",
          ctaPrimary: "bg-cyan-300 hover:bg-cyan-400 text-slate-900",
          protoBoxBg: "from-cyan-50 to-indigo-50",
          protoBoxBorder: "border-cyan-500",
          protoBoxLabel: "text-cyan-700",
          protoStepBadge: "bg-cyan-500",
          risultatoBg: "bg-indigo-600",
          iconGradient: "from-cyan-500 to-indigo-600",
          eyebrowAccent: "text-cyan-700",
          benefitCard: "from-cyan-500 to-indigo-600",
          ctaBanner: "from-cyan-400 to-indigo-500",
        }
      : {
          pageBg: "bg-gradient-to-b from-sky-50 via-white to-blue-50",
          heroOverlay: "bg-gradient-to-r from-slate-900/80 via-sky-900/55 to-transparent",
          heroEyebrow: "text-sky-200",
          heroSubtitle: "text-sky-100",
          ctaPrimary: "bg-sky-300 hover:bg-sky-400 text-slate-900",
          protoBoxBg: "from-sky-50 to-blue-50",
          protoBoxBorder: "border-sky-500",
          protoBoxLabel: "text-sky-700",
          protoStepBadge: "bg-sky-500",
          risultatoBg: "bg-blue-700",
          iconGradient: "from-sky-500 to-blue-700",
          eyebrowAccent: "text-sky-700",
          benefitCard: "from-sky-500 to-blue-700",
          ctaBanner: "from-sky-400 to-blue-600",
        })
    : (isParrucchieri
      ? {
          pageBg: "bg-gradient-to-b from-fuchsia-50 via-white to-violet-50",
          heroOverlay: "bg-gradient-to-r from-violet-900/75 via-fuchsia-800/55 to-transparent",
          heroEyebrow: "text-fuchsia-200",
          heroSubtitle: "text-fuchsia-100",
          ctaPrimary: "bg-fuchsia-400 hover:bg-fuchsia-500 text-slate-900",
          protoBoxBg: "from-fuchsia-50 to-violet-50",
          protoBoxBorder: "border-fuchsia-500",
          protoBoxLabel: "text-fuchsia-700",
          protoStepBadge: "bg-fuchsia-500",
          risultatoBg: "bg-violet-600",
          iconGradient: "from-fuchsia-500 to-violet-600",
          eyebrowAccent: "text-fuchsia-600",
          benefitCard: "from-fuchsia-500 to-violet-600",
          ctaBanner: "from-fuchsia-500 to-violet-600",
        }
      : {
          pageBg: "bg-gradient-to-b from-orange-50 via-white to-amber-50",
          heroOverlay: "bg-gradient-to-r from-orange-900/75 via-amber-800/55 to-transparent",
          heroEyebrow: "text-amber-200",
          heroSubtitle: "text-amber-100",
          ctaPrimary: "bg-amber-400 hover:bg-amber-500 text-slate-900",
          protoBoxBg: "from-emerald-50 to-teal-50",
          protoBoxBorder: "border-emerald-500",
          protoBoxLabel: "text-emerald-700",
          protoStepBadge: "bg-emerald-500",
          risultatoBg: "bg-teal-600",
          iconGradient: "from-emerald-500 to-teal-600",
          eyebrowAccent: "text-emerald-600",
          benefitCard: "from-emerald-500 to-teal-600",
          ctaBanner: "from-amber-400 to-orange-500",
        });

  return (
    <div className={`min-h-screen ${theme.pageBg}`}>
      {/* Admin edit bar */}
      {admin && (
        <div className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur text-white px-4 py-2 flex items-center justify-between shadow-lg" data-testid="admin-edit-bar">
          <span className="text-xs font-medium opacity-80">🛠️ Admin — {isParrucchieri ? "Landing Protocollo Salone Parrucchieri" : "Landing Protocollo Cabina Estetiste"}{isInverno ? " (Inverno)" : ""}</span>
          <div className="flex gap-2">
            {editing ? (
              <>
                <Button size="sm" onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-save-landing">
                  <Save className="w-4 h-4 mr-1" /> {saving ? "Salvo..." : "Salva"}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(false)} className="text-slate-900" data-testid="btn-cancel-landing">
                  <X className="w-4 h-4 mr-1" /> Annulla
                </Button>
                <Button size="sm" variant="outline" onClick={resetDefault} className="text-red-600" data-testid="btn-reset-landing">
                  <RotateCcw className="w-4 h-4 mr-1" /> Reset default
                </Button>
              </>
            ) : (
              <>
                <Button size="sm" onClick={() => setEditing(true)} className="bg-amber-500 hover:bg-amber-600" data-testid="btn-edit-landing">
                  <Pencil className="w-4 h-4 mr-1" /> Modifica testi e immagini
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-slate-900 bg-white/90 hover:bg-white"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem("token");
                      const res = await fetch(`${API}/landing/admin/export-all`, { headers: { Authorization: `Bearer ${token}` } });
                      const data = await res.json();
                      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `landing-backup-${new Date().toISOString().slice(0,10)}.json`;
                      a.click();
                      URL.revokeObjectURL(url);
                      toast.success(`Backup scaricato: ${data.count} landing`);
                    } catch (e) { toast.error("Errore export"); }
                  }}
                  data-testid="btn-export-landing"
                  title="Scarica JSON con tutte le landing (per backup o sync Preview→Production)"
                >
                  ⬇️ Export
                </Button>
                <label
                  className="cursor-pointer inline-flex items-center gap-1 h-8 px-3 rounded-md text-sm font-medium bg-white/90 text-slate-900 hover:bg-white border border-input"
                  data-testid="btn-import-landing"
                  title="Carica un JSON esportato per ripristinare contenuti+immagini"
                >
                  ⬆️ Import
                  <input
                    type="file"
                    accept=".json,application/json"
                    className="hidden"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      if (!window.confirm(`Sovrascrivere tutte le landing con il contenuto di "${f.name}"?`)) { e.target.value = ""; return; }
                      try {
                        const text = await f.text();
                        const payload = JSON.parse(text);
                        const token = localStorage.getItem("token");
                        const res = await fetch(`${API}/landing/admin/import-all`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                          body: JSON.stringify({ landings: payload.landings || [], overwrite: true }),
                        });
                        const data = await res.json();
                        if (data.success) {
                          toast.success(`Import OK: ${data.created} create, ${data.updated} aggiornate`);
                          setTimeout(() => window.location.reload(), 1000);
                        } else {
                          toast.error(data.detail || "Errore import");
                        }
                      } catch (err) {
                        toast.error("File non valido: " + err.message);
                      }
                      e.target.value = "";
                    }}
                  />
                </label>
              </>
            )}
          </div>
        </div>
      )}

      {/* Sticky top bar con navigazione bidirezionale — visibile a tutti */}
      {(() => {
        const siblingSlug = data.sibling_slug || SIBLING_FALLBACK[SLUG]?.slug;
        const siblingLabel = data.sibling_label || SIBLING_FALLBACK[SLUG]?.label;
        const seasonSlug = data.season_link_slug || SEASON_FALLBACK[SLUG]?.slug;
        const seasonLabel = data.season_link_label || SEASON_FALLBACK[SLUG]?.label;
        const showBar = siblingSlug || seasonSlug;
        if (!showBar) return null;
        return (
        <div className={`w-full ${isInverno ? (isParrucchieri ? "bg-indigo-950" : "bg-slate-900") : (isParrucchieri ? "bg-violet-950" : "bg-emerald-950")} text-white py-3 px-4 shadow-md`} data-testid="bidirectional-topbar">
          <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-3">
            <span className="text-sm font-medium opacity-90 hidden sm:inline">
              {isInverno ? "❄️ " : (isParrucchieri ? "💇 " : "🌿 ")}
              {isParrucchieri ? "Sezione Parrucchieri" : "Sezione Centri Estetici"}
              {isInverno ? " — Inverno" : " — Estate"}
            </span>
            <div className="flex flex-wrap items-center gap-2">
              {seasonSlug && SLUG_TO_ROUTE[seasonSlug] && (
                <Link
                  to={SLUG_TO_ROUTE[seasonSlug]}
                  className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/25 border border-white/40 text-white text-xs md:text-sm font-semibold px-4 py-2 rounded-full transition-all hover:scale-105"
                  data-testid="btn-season-link"
                >
                  {isInverno ? "☀️" : "❄️"}
                  {seasonLabel}
                </Link>
              )}
              {siblingSlug && SLUG_TO_ROUTE[siblingSlug] && (
                <Link
                  to={SLUG_TO_ROUTE[siblingSlug]}
                  className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/30 border-2 border-white/50 text-white text-sm font-bold px-6 py-2.5 rounded-full transition-all hover:scale-105 shadow-lg"
                  data-testid="btn-sibling-landing"
                >
                  <ArrowLeftRight className="w-4 h-4" />
                  {siblingLabel} →
                </Link>
              )}
            </div>
          </div>
        </div>
        );
      })()}

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <EditableImage url={data.hero?.image} onChange={(v) => patch("hero.image", v)} editing={editing} alt="Protocollo ODR" className="w-full h-full object-cover opacity-70" />
          <div className={`absolute inset-0 ${theme.heroOverlay}`}></div>
        </div>
        <div className="relative max-w-6xl mx-auto px-6 py-24 lg:py-32">
          <div className="max-w-3xl text-white">
            {data.hero?.logo && (
              <img src={data.hero.logo} alt="Logo ODR" className="h-16 md:h-20 mb-6 drop-shadow-xl" data-testid="hero-logo" />
            )}
            <p className={`uppercase tracking-widest text-sm font-semibold ${theme.heroEyebrow} mb-3`} data-testid="hero-eyebrow">
              <EditableText value={data.hero?.eyebrow} onChange={(v) => patch("hero.eyebrow", v)} editing={editing} />
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-3 leading-tight" data-testid="hero-title">
              <EditableText value={data.hero?.title} onChange={(v) => patch("hero.title", v)} editing={editing} />
            </h1>
            <p className={`text-2xl lg:text-3xl font-light ${theme.heroSubtitle} mb-6 tracking-wide`} data-testid="hero-subtitle">
              <EditableText value={data.hero?.subtitle} onChange={(v) => patch("hero.subtitle", v)} editing={editing} />
            </p>
            <p className="text-lg text-white/90 mb-8 leading-relaxed max-w-2xl" data-testid="hero-description">
              <EditableText value={data.hero?.description} onChange={(v) => patch("hero.description", v)} editing={editing} multiline />
            </p>
            <div className="flex flex-wrap gap-3">
              <Button size="lg" className={`${theme.ctaPrimary} font-bold rounded-full px-8`}>
                <EditableText value={data.hero?.cta_primary} onChange={(v) => patch("hero.cta_primary", v)} editing={editing} /> <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Link to="/prodotti">
                <Button size="lg" variant="outline" className="bg-white/10 border-white/40 text-white hover:bg-white/20 rounded-full px-8">
                  <EditableText value={data.hero?.cta_secondary} onChange={(v) => patch("hero.cta_secondary", v)} editing={editing} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* INDICAZIONI */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold text-slate-800 mb-8 text-center">
          {isParrucchieri ? "Indicato per Parrucchieri su" : "Indicato per Estetiste su"}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {(data.indicazioni || []).map((it, i) => (
            <div key={i} className="bg-white border border-emerald-100 rounded-xl p-4 flex items-center gap-3 shadow-sm hover:shadow-md transition">
              <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0"></span>
              <span className="text-sm text-slate-700 font-medium">
                <EditableText value={it} onChange={(v) => patchArrayItem("indicazioni", i, "", v)} editing={editing} />
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* CASI CLINICI */}
      <section className="max-w-6xl mx-auto px-6 py-12 space-y-16">
        {(data.casi || []).map((c, i) => {
          const IconCmp = iconForCase(c.numero);
          const flip = i % 2 === 1;
          return (
            <div key={i} className={`grid md:grid-cols-2 gap-8 items-center ${flip ? "md:[&>*:first-child]:order-2" : ""}`} data-testid={`caso-${c.numero}`}>
              <div className="rounded-2xl overflow-hidden shadow-2xl aspect-[4/3] relative bg-slate-50 ring-1 ring-slate-200">
                <EditableImage url={c.image} onChange={(v) => patchArrayItem("casi", i, "image", v)} editing={editing} alt={c.titolo} className="w-full h-full object-contain p-2" />
              </div>
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${theme.iconGradient} flex items-center justify-center text-white shadow-lg`}>
                    <IconCmp className="w-6 h-6" />
                  </div>
                  <div>
                    <p className={`text-xs ${theme.eyebrowAccent} font-bold tracking-wider`}>CASO {c.numero}</p>
                    <h3 className="text-2xl font-bold text-slate-800"><EditableText value={c.titolo} onChange={(v) => patchArrayItem("casi", i, "titolo", v)} editing={editing} /></h3>
                    <p className="text-sm text-slate-500 italic"><EditableText value={c.sottotitolo} onChange={(v) => patchArrayItem("casi", i, "sottotitolo", v)} editing={editing} /></p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-5">
                  <div className="bg-rose-50 rounded-xl p-4">
                    <p className="text-xs font-bold text-rose-700 uppercase mb-2">Cause</p>
                    <ul className="text-sm text-slate-700 space-y-1">
                      {(c.cause || []).map((x, j) => <li key={j}>• <EditableText value={x} onChange={(v) => { const arr = [...c.cause]; arr[j] = v; patchArrayItem("casi", i, "cause", arr); }} editing={editing} /></li>)}
                    </ul>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-4">
                    <p className="text-xs font-bold text-amber-700 uppercase mb-2">Segni</p>
                    <ul className="text-sm text-slate-700 space-y-1">
                      {(c.segni || []).map((x, j) => <li key={j}>• <EditableText value={x} onChange={(v) => { const arr = [...c.segni]; arr[j] = v; patchArrayItem("casi", i, "segni", arr); }} editing={editing} /></li>)}
                    </ul>
                  </div>
                </div>

                <div className={`bg-gradient-to-br ${theme.protoBoxBg} rounded-xl p-5 mb-4 border-l-4 ${theme.protoBoxBorder}`}>
                  <p className={`text-xs font-bold ${theme.protoBoxLabel} uppercase mb-3`}>
                    {isParrucchieri ? "Protocollo in Salone" : "Protocollo in Cabina"}
                  </p>
                  <ol className="space-y-2">
                    {(c.fasi || []).map((f, j) => (
                      <li key={j} className="flex gap-3">
                        <span className={`w-7 h-7 rounded-full ${theme.protoStepBadge} text-white text-xs font-bold flex items-center justify-center flex-shrink-0`}>{f.n}</span>
                        <div className="text-sm">
                          <b className="text-slate-800"><EditableText value={f.nome} onChange={(v) => { const arr = [...c.fasi]; arr[j] = {...f, nome: v}; patchArrayItem("casi", i, "fasi", arr); }} editing={editing} /></b>
                          {f.desc && <div className="text-slate-600 text-xs mt-0.5">
                            <EditableText value={f.desc} onChange={(v) => { const arr = [...c.fasi]; arr[j] = {...f, desc: v}; patchArrayItem("casi", i, "fasi", arr); }} editing={editing} />
                          </div>}
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>

                <div className={`${theme.risultatoBg} text-white rounded-xl p-4`}>
                  <p className="text-xs font-bold uppercase opacity-80 mb-2">Risultato</p>
                  <ul className="text-sm space-y-1">
                    {(c.risultato || []).map((x, j) => <li key={j}>✓ <EditableText value={x} onChange={(v) => { const arr = [...c.risultato]; arr[j] = v; patchArrayItem("casi", i, "risultato", arr); }} editing={editing} /></li>)}
                  </ul>
                </div>

                <CertificationBadgesRow
                  accentClass={isParrucchieri ? "text-fuchsia-600" : "text-emerald-600"}
                  borderClass={isParrucchieri ? "border-fuchsia-200" : "border-emerald-200"}
                  bgClass={isParrucchieri ? "bg-fuchsia-50/60" : "bg-emerald-50/60"}
                />
              </div>
            </div>
          );
        })}
      </section>

      {/* LINEE GUIDA */}
      <section className="bg-slate-900 text-white py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-3"><EditableText value={data.linee_guida?.titolo} onChange={(v) => patch("linee_guida.titolo", v)} editing={editing} /></h2>
          <p className="text-slate-400 mb-8">Regole d'oro per applicare il Protocollo ODR</p>
          <div className="grid md:grid-cols-2 gap-3 text-left">
            {(data.linee_guida?.punti || []).map((p, i) => (
              <div key={i} className="bg-white/5 rounded-lg p-4 border border-white/10 flex gap-3 items-start">
                <span className="w-6 h-6 rounded-full bg-emerald-500 text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                <span className="text-sm"><EditableText value={p} onChange={(v) => patchArrayItem("linee_guida.punti", i, "", v)} editing={editing} /></span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFICI */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold text-slate-800 text-center mb-3">Benefici Globali del Protocollo ODR</h2>
        <p className="text-slate-500 text-center mb-10">Con uso in cabina + mantenimento domiciliare</p>
        <div className="grid md:grid-cols-3 gap-4">
          {(data.benefici || []).map((b, i) => (
            <div key={i} className={`bg-gradient-to-br ${theme.benefitCard} text-white rounded-2xl p-5 shadow-lg`}>
              <div className="text-3xl mb-2">✔</div>
              <p className="font-medium text-sm leading-relaxed"><EditableText value={b} onChange={(v) => patchArrayItem("benefici", i, "", v)} editing={editing} /></p>
            </div>
          ))}
        </div>
      </section>

      {/* LINEA PRODOTTI ODR — estratti dalla brochure ufficiale */}
      {(data.prodotti || []).length > 0 && (
        <section className={`${theme.pageBg.includes("indigo") || theme.pageBg.includes("sky") ? "bg-white" : "bg-white"} py-16`} data-testid="linea-prodotti-section">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <p className={`text-xs uppercase tracking-widest font-bold ${theme.eyebrowAccent} mb-2`}>La Linea Ufficiale</p>
              <h2 className="text-3xl lg:text-4xl font-black text-slate-900 mb-3">
                Prodotti ODR — {isParrucchieri ? "Linea Hair" : "Linea Estetica"}
              </h2>
              <p className="text-slate-600 text-lg max-w-2xl mx-auto">
                {isParrucchieri
                  ? "Cosmetici tricologici formulati per proteggere e ricostruire capelli e cuoio capelluto fragili."
                  : "Cosmetici formulati per pelli sensibili, fragili o compromesse da terapie farmacologiche e oncologiche."}
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(data.prodotti || []).map((p, i) => (
                <div key={i} className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-slate-200 overflow-hidden hover:-translate-y-1" data-testid={`prodotto-${i}`}>
                  <div className={`bg-gradient-to-r ${theme.iconGradient} h-2`} />
                  <div className="p-6">
                    <p className={`text-xs font-bold uppercase tracking-wider ${theme.eyebrowAccent} mb-2`}>
                      <EditableText value={p.categoria} onChange={(v) => patchArrayItem("prodotti", i, "categoria", v)} editing={editing} />
                    </p>
                    <h3 className="text-lg font-black text-slate-900 mb-3 leading-tight">
                      <EditableText value={p.nome} onChange={(v) => patchArrayItem("prodotti", i, "nome", v)} editing={editing} />
                    </h3>
                    <p className="text-sm text-slate-700 leading-relaxed mb-4">
                      <EditableText value={p.descrizione} onChange={(v) => patchArrayItem("prodotti", i, "descrizione", v)} editing={editing} multiline />
                    </p>
                    <div className="mb-4">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Principi attivi chiave</p>
                      <div className="flex flex-wrap gap-1.5">
                        {(p.ingredienti || []).slice(0, 6).map((ing, j) => (
                          <span key={j} className="text-[11px] bg-slate-100 text-slate-700 border border-slate-200 rounded-full px-2.5 py-0.5 font-medium">
                            {ing}
                          </span>
                        ))}
                        {(p.ingredienti || []).length > 6 && (
                          <span className="text-[11px] text-slate-500 font-medium px-2.5 py-0.5">
                            +{p.ingredienti.length - 6}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="border-t border-slate-100 pt-3 space-y-2">
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Modo d'uso</p>
                        <p className="text-xs text-slate-600 leading-relaxed">
                          <EditableText value={p.uso} onChange={(v) => patchArrayItem("prodotti", i, "uso", v)} editing={editing} multiline />
                        </p>
                      </div>
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Indicato per</p>
                        <p className="text-xs text-slate-600 leading-relaxed italic">
                          <EditableText value={p.indicazioni} onChange={(v) => patchArrayItem("prodotti", i, "indicazioni", v)} editing={editing} multiline />
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SICUREZZA & FORMULE — Prodotti privi di sostanze nocive */}
      <section className="bg-slate-50 py-16 border-y border-slate-200" data-testid="sicurezza-formule">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Sicurezza & Trasparenza</p>
            <h2 className="text-3xl lg:text-4xl font-black text-slate-900 mb-3">
              Prodotti privi di sostanze nocive
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Le formule ODR sono pensate per la pelle più vulnerabile: massima efficacia, zero compromessi.
            </p>
          </div>
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-10 border border-slate-100">
            <ul className="grid gap-5 md:gap-4">
              {[
                { txt: <><b>Alta concentrazione di IOXINA<sup>®</sup></b> — principio attivo esclusivo ODR</>, testid: "formula-ioxina" },
                { txt: <>Zero profumi sintetici</>, testid: "formula-profumi" },
                { txt: <><b>Senza parabeni, PEG, SLES, coloranti o alcol</b></>, testid: "formula-nocivi" },
                { txt: <>Formule delicate, ma potenti, pensate per una pelle profondamente vulnerabile</>, testid: "formula-delicate" },
                { txt: <>Risultati visibili: pelle più elastica, lenita e nutrita già dopo le prime applicazioni</>, testid: "formula-risultati" },
              ].map((p, i) => (
                <li key={i} className="flex gap-4 items-start text-slate-800 text-base md:text-lg leading-relaxed" data-testid={p.testid}>
                  <span className={`flex-shrink-0 w-8 h-8 md:w-9 md:h-9 rounded-full bg-gradient-to-br ${theme.iconGradient} text-white flex items-center justify-center font-black shadow-md`}>✓</span>
                  <span>{p.txt}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PITTOGRAMMI CERTIFICAZIONI — grandi seal stile bollino */}
      <section className="bg-white py-16" data-testid="certificazioni-visual">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Certificazioni & Test</p>
            <h2 className="text-3xl lg:text-4xl font-black text-slate-900 mb-3">
              Testati e Certificati
            </h2>
            <p className="text-slate-600 text-lg">
              Ogni prodotto ODR è sottoposto a rigorosi test dermatologici e di sicurezza
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 max-w-4xl mx-auto text-slate-900">
            {CERT_SEALS.map((s, i) => (
              <div key={i} className="flex flex-col items-center" data-testid={`cert-seal-${i}`}>
                <div className="w-32 h-32 md:w-40 md:h-40">
                  <Seal topText={s.topText} bottomText={s.bottomText}>{s.inner}</Seal>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LOGHI UNIVERSITÀ */}
      <section className="bg-slate-50 py-16 border-t border-slate-200" data-testid="universita-section">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Ricerca Scientifica</p>
          <h2 className="text-3xl lg:text-4xl font-black text-slate-900 mb-3">
            Testato con le Università Italiane
          </h2>
          <p className="text-slate-600 text-lg mb-10 max-w-2xl mx-auto">
            La linea ODR è studiata in collaborazione con dipartimenti universitari di eccellenza
          </p>
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-14">
            {UNIVERSITA_LOGOS.map((u, i) => (
              <div key={i} className="grayscale hover:grayscale-0 transition-all duration-300 opacity-80 hover:opacity-100" title={u.name}>
                <img
                  src={u.url}
                  alt={u.name}
                  className="h-20 md:h-24 w-auto object-contain"
                  loading="lazy"
                  data-testid={`universita-logo-${i}`}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DISPENSE SCARICABILI */}
      <section className="max-w-6xl mx-auto px-6 py-16" data-testid="dispense-section">
        <h2 className="text-3xl font-bold text-slate-800 text-center mb-3">
          📚 Dispense Scaricabili {isParrucchieri ? "per Parrucchieri" : "per Centri Estetici"}
        </h2>
        <p className="text-slate-500 text-center mb-10">
          Manuali pronti da consultare o stampare per la formazione del tuo team
        </p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {(data.dispense || []).length === 0 && !editing && (
            <div className="col-span-full text-center text-slate-400 py-8">
              Nessuna dispensa disponibile al momento.
            </div>
          )}
          {(data.dispense || []).map((disp, i) => (
            <div key={i} className="bg-white border-2 border-slate-200 hover:border-slate-400 rounded-2xl p-5 shadow-sm hover:shadow-lg transition group" data-testid={`dispensa-${i}`}>
              <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${theme.iconGradient} text-white text-2xl mb-4`}>📄</div>
              <h3 className="text-lg font-bold text-slate-800 mb-2">
                <EditableText value={disp.titolo} onChange={(v) => patchArrayItem("dispense", i, "titolo", v)} editing={editing} />
              </h3>
              <p className="text-sm text-slate-600 mb-4 leading-relaxed">
                <EditableText value={disp.descrizione} onChange={(v) => patchArrayItem("dispense", i, "descrizione", v)} editing={editing} multiline />
              </p>
              {editing && (
                <Input
                  value={disp.url || ""}
                  onChange={(e) => patchArrayItem("dispense", i, "url", e.target.value)}
                  placeholder="URL PDF (es. /uploads/dispensa.pdf)"
                  className="text-xs bg-slate-50 mb-2"
                  data-testid={`dispensa-${i}-url`}
                />
              )}
              <a
                href={disp.url || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-2 text-sm font-semibold ${theme.eyebrowAccent} group-hover:underline`}
                data-testid={`dispensa-${i}-download`}
                onClick={(e) => { if (!disp.url) { e.preventDefault(); toast.error("Link non disponibile"); } }}
              >
                Scarica <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          ))}
        </div>
        {editing && (
          <div className="text-center mt-6">
            <Button
              variant="outline"
              onClick={() => {
                const nuova = { titolo: "Nuova dispensa", descrizione: "Descrizione breve", url: "" };
                setData((prev) => ({ ...prev, dispense: [...(prev.dispense || []), nuova] }));
              }}
              data-testid="btn-add-dispensa"
            >
              + Aggiungi dispensa
            </Button>
          </div>
        )}
      </section>

      {/* DISPENSE ADMIN — visibili solo agli admin */}
      {admin && (data.dispense_admin || []).length > 0 && (
        <section className="max-w-6xl mx-auto px-6 pb-16" data-testid="dispense-admin-section">
          <div className="bg-slate-900 rounded-2xl p-6 md:p-8 text-white shadow-2xl border-2 border-amber-500/30">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-2xl">🔒</span>
              <div>
                <h3 className="text-xl font-bold text-amber-300">Materiale Riservato — Solo Amministratori</h3>
                <p className="text-sm text-slate-400">Documenti interni per formatori e staff ODR. Non condividere con le corsiste.</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
              {(data.dispense_admin || []).map((disp, i) => (
                <div key={i} className="bg-slate-800/60 border border-amber-500/20 rounded-xl p-4" data-testid={`dispensa-admin-${i}`}>
                  <h4 className="font-bold text-amber-100 mb-2">
                    <EditableText value={disp.titolo} onChange={(v) => patchArrayItem("dispense_admin", i, "titolo", v)} editing={editing} />
                  </h4>
                  <p className="text-xs text-slate-300 mb-3 leading-relaxed">
                    <EditableText value={disp.descrizione} onChange={(v) => patchArrayItem("dispense_admin", i, "descrizione", v)} editing={editing} multiline />
                  </p>
                  {editing && (
                    <Input
                      value={disp.url || ""}
                      onChange={(e) => patchArrayItem("dispense_admin", i, "url", e.target.value)}
                      placeholder="URL"
                      className="text-xs bg-slate-700 text-white border-slate-600 mb-2"
                    />
                  )}
                  <a
                    href={disp.url || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-amber-300 hover:text-amber-200 hover:underline"
                    onClick={(e) => { if (!disp.url) { e.preventDefault(); toast.error("Link non disponibile"); } }}
                  >
                    Scarica <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CONCETTO CHIAVE */}
      <section className={`bg-gradient-to-br ${theme.ctaBanner} py-16`}>
        <div className="max-w-4xl mx-auto px-6 text-center text-slate-900">
          <p className="text-xs uppercase tracking-widest font-bold mb-2">Concetto chiave</p>
          <h2 className="text-3xl md:text-4xl font-black mb-6">
            <EditableText value={data.concetto_chiave?.titolo} onChange={(v) => patch("concetto_chiave.titolo", v)} editing={editing} />
          </h2>
          <p className="text-xl leading-relaxed mb-8 font-medium">
            <EditableText value={data.concetto_chiave?.testo} onChange={(v) => patch("concetto_chiave.testo", v)} editing={editing} multiline />
          </p>
          <div className="inline-block bg-slate-900 text-white rounded-full px-8 py-4 shadow-2xl">
            <p className="font-bold uppercase tracking-wide text-sm">
              <EditableText value={data.concetto_chiave?.call_to_action} onChange={(v) => patch("concetto_chiave.call_to_action", v)} editing={editing} />
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
